import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["name", "label", "placeholder", "groupLabels", "showGroupDivider", "groupDivider", "options", "value", "onChange", "onMenuOpen", "onMenuClose", "virtualized", "virtualizedContainerHeight", "virtualizedContainerWidth", "enableItemSearch", "legacyMUIMenuEnabled", "itemSearchDebounceMs", "disableVirtualizationOnItemCountLowerThan"];
import React, { useRef, useState } from 'react';
import { SelectStyles } from "./Select.styles.js";
import { SelectMenuOptions } from "./SelectMenuOptions.js";
import { SelectPortalMenu } from "./SelectPortalMenu.js";
import { useSelectOptionsFilter } from "./useSelectOptionsFilter.js";
var noop = function noop() {
  return undefined;
};
var KEY_ESCAPE = 27;
var KEY_SPACE = 32;
var KEY_ENTER = 13;
var KEY_TAB = 9;
var KEY_ARROW_UP = 38;
var KEY_ARROW_DOWN = 40;
export var Select = function Select(_ref) {
  var _anchorRef$current2;
  var name = _ref.name,
    label = _ref.label,
    placeholder = _ref.placeholder,
    groupLabels = _ref.groupLabels,
    _ref$showGroupDivider = _ref.showGroupDivider,
    showGroupDivider = _ref$showGroupDivider === void 0 ? false : _ref$showGroupDivider,
    _ref$groupDivider = _ref.groupDivider,
    groupDivider = _ref$groupDivider === void 0 ? null : _ref$groupDivider,
    options = _ref.options,
    value = _ref.value,
    onChange = _ref.onChange,
    _ref$onMenuOpen = _ref.onMenuOpen,
    onMenuOpen = _ref$onMenuOpen === void 0 ? noop : _ref$onMenuOpen,
    _ref$onMenuClose = _ref.onMenuClose,
    onMenuClose = _ref$onMenuClose === void 0 ? noop : _ref$onMenuClose,
    _ref$virtualized = _ref.virtualized,
    virtualized = _ref$virtualized === void 0 ? false : _ref$virtualized,
    _ref$virtualizedConta = _ref.virtualizedContainerHeight,
    virtualizedContainerHeight = _ref$virtualizedConta === void 0 ? 200 : _ref$virtualizedConta,
    virtualizedContainerWidth = _ref.virtualizedContainerWidth,
    _ref$enableItemSearch = _ref.enableItemSearch,
    enableItemSearch = _ref$enableItemSearch === void 0 ? true : _ref$enableItemSearch,
    _ref$legacyMUIMenuEna = _ref.legacyMUIMenuEnabled,
    legacyMUIMenuEnabled = _ref$legacyMUIMenuEna === void 0 ? false : _ref$legacyMUIMenuEna,
    itemSearchDebounceMs = _ref.itemSearchDebounceMs,
    _ref$disableVirtualiz = _ref.disableVirtualizationOnItemCountLowerThan,
    disableVirtualizationOnItemCountLowerThan = _ref$disableVirtualiz === void 0 ? 20 : _ref$disableVirtualiz,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchText = _useState2[0],
    setSearchText = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isMenuOpen = _useState4[0],
    setIsMenuOpen = _useState4[1];
  var anchorRef = useRef(null);
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    cursor = _useState6[0],
    setCursor = _useState6[1];
  var _useSelectOptionsFilt = useSelectOptionsFilter({
      options: options,
      searchText: searchText,
      groupLabels: groupLabels,
      debounceMs: itemSearchDebounceMs
    }),
    groups = _useSelectOptionsFilt.groups,
    allGroupLabels = _useSelectOptionsFilt.allGroupLabels,
    sortedFilteredOptions = _useSelectOptionsFilt.sortedFilteredOptions;
  var handleClose = function handleClose() {
    var _anchorRef$current;
    setIsMenuOpen(false);
    onMenuClose();
    (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 || _anchorRef$current.focus();
  };
  var handleOpen = function handleOpen() {
    var selectedIndex = sortedFilteredOptions.findIndex(function (each) {
      return each.value === value;
    });
    if (selectedIndex > -1) {
      var _sortedFilteredOption;
      setCursor((_sortedFilteredOption = sortedFilteredOptions[selectedIndex].index) !== null && _sortedFilteredOption !== void 0 ? _sortedFilteredOption : selectedIndex);
    }
    setIsMenuOpen(true);
    onMenuOpen();
  };
  var handleOptionSelection = function handleOptionSelection(newValue) {
    onChange(newValue);
    handleClose();
  };
  var handleSearchText = function handleSearchText(text) {
    setSearchText(text);
    setCursor(0);
  };
  var disableVirtualizationDueToFewItems = virtualized && sortedFilteredOptions.length < disableVirtualizationOnItemCountLowerThan;
  var isVirtualizationActive = virtualized && !disableVirtualizationDueToFewItems;
  var buildOptions = function buildOptions() {
    return /*#__PURE__*/React.createElement(SelectMenuOptions, {
      value: value,
      filteredOptions: sortedFilteredOptions,
      groups: groups,
      cursor: cursor,
      groupLabels: allGroupLabels,
      showGroupDivider: showGroupDivider,
      groupDivider: groupDivider,
      virtualized: isVirtualizationActive,
      virtualizedContainerHeight: virtualizedContainerHeight,
      virtualizedContainerWidth: virtualizedContainerWidth,
      onOptionSelection: handleOptionSelection
    });
  };

  // eslint-disable-next-line no-nested-ternary
  var _ref2 = function () {
      if (!virtualized) {
        return ['264px', undefined];
      }
      if (disableVirtualizationDueToFewItems) {
        return ["".concat(virtualizedContainerHeight, "px"), virtualizedContainerWidth ? "".concat(virtualizedContainerWidth, "px") : 'auto'];
      }
      return [undefined, undefined];
    }(),
    _ref3 = _slicedToArray(_ref2, 2),
    portalMenuMaxHeight = _ref3[0],
    portalMenuMaxWidth = _ref3[1];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SelectStyles.SelectFieldStyled, _extends({
    "data-testid": "select"
  }, otherProps, {
    label: label,
    placeholder: placeholder,
    "aria-haspopup": "listbox",
    endAdornment: /*#__PURE__*/React.createElement(SelectStyles.IconArrow, {
      $isFlipped: isMenuOpen,
      $disabled: otherProps.disabled || false,
      size: 16
    }),
    ref: anchorRef,
    isShownAsFocused: isMenuOpen,
    selectedOption: sortedFilteredOptions.find(function (option) {
      return option.value === value;
    }),
    onClick: isMenuOpen ? handleClose : handleOpen,
    name: name,
    onKeyUp: function onKeyUp(_ref4) {
      var keyCode = _ref4.keyCode;
      if (keyCode === KEY_TAB && document.activeElement === anchorRef.current) {
        handleOpen();
      }
    },
    showSearchInput: isMenuOpen && enableItemSearch && !legacyMUIMenuEnabled,
    onSearchText: handleSearchText,
    onKeyDown: function onKeyDown(evt) {
      var keyCode = evt.keyCode;
      if (legacyMUIMenuEnabled) {
        return;
      }
      switch (keyCode) {
        case KEY_ESCAPE:
          handleClose();
          break;
        case KEY_SPACE:
          if (!isMenuOpen) {
            handleOpen();
            evt.preventDefault();
          }
          break;
        case KEY_ENTER:
          evt.preventDefault();
          handleOptionSelection(sortedFilteredOptions[cursor].value);
          break;
        case KEY_TAB:
          if (isMenuOpen) {
            setIsMenuOpen(false);
            onMenuClose();
          }
          break;
        case KEY_ARROW_UP:
          if (cursor > 0) {
            setCursor(function (prevCursor) {
              return prevCursor - 1;
            });
          } else if (!isVirtualizationActive) {
            setCursor(sortedFilteredOptions.length - 1);
          }
          evt.preventDefault();
          break;
        case KEY_ARROW_DOWN:
          if (cursor < sortedFilteredOptions.length - 1) {
            setCursor(cursor + 1);
          } else if (!isVirtualizationActive) {
            setCursor(0);
          }
          evt.preventDefault();
          break;
        default:
          break;
      }
    }
  })), legacyMUIMenuEnabled ? /*#__PURE__*/React.createElement(SelectStyles.MenuStyled, {
    $minWidth: (_anchorRef$current2 = anchorRef.current) === null || _anchorRef$current2 === void 0 ? void 0 : _anchorRef$current2.clientWidth,
    $hasMaxHeight: !isVirtualizationActive,
    anchorEl: anchorRef.current,
    isOpen: isMenuOpen,
    onClose: handleClose,
    role: "listbox",
    className: otherProps.className
  }, buildOptions()) : /*#__PURE__*/React.createElement(SelectPortalMenu, {
    anchorElement: anchorRef.current,
    isOpen: isMenuOpen,
    isVirtualizationActive: isVirtualizationActive,
    cursor: cursor,
    maxHeight: portalMenuMaxHeight,
    maxWidth: portalMenuMaxWidth,
    excludeElementsFromOutsideClick: [anchorRef.current],
    onOutsideMenuClick: handleClose
  }, buildOptions()));
};